/**
 * EVO — i18n uchun React qatlami.
 *
 * `i18n.ts` sof funksiyalar (React'siz), bu fayl esa til o'zgarganda
 * qayta render qilishni ta'minlaydi. Ajratilgan sabab: `api.ts` va boshqa
 * React'siz modullar `t()` ni to'g'ridan-to'g'ri chaqira olsin.
 */
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_LOCALE,
  getLocale,
  isLocale,
  setLocale as setModuleLocale,
  translate,
  type Locale,
  type TranslationKey,
  type TranslationParams,
} from './i18n';

const LOCALE_STORAGE_KEY = 'evo.locale';

interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: (key: TranslationKey, params?: TranslationParams) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // localStorage bloklangan bo'lishi mumkin (private rejim) — default bilan davom etamiz.
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = readStoredLocale();
    setModuleLocale(initial);
    return initial;
  });

  const setLocale = useCallback((next: Locale) => {
    setModuleLocale(next);
    setLocaleState(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // Saqlab bo'lmasa ham til shu sessiyada ishlaydi.
    }
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => translate(locale, key, params),
    }),
    [locale, setLocale],
  );

  return <I18nContext value={value}>{children}</I18nContext>;
}

export function useI18n(): I18nContextValue {
  const ctx = use(I18nContext);
  if (!ctx) {
    throw new Error('useI18n faqat <I18nProvider> ichida ishlatiladi');
  }
  return ctx;
}

/** Komponentlarda asosiy ishlatilish shakli: `const t = useT()`. */
export function useT(): (key: TranslationKey, params?: TranslationParams) => string {
  return useI18n().t;
}

/** Foydalanuvchi profilidagi til (users.locale) bilan sinxronlash uchun. */
export function syncLocaleFromProfile(locale: string | undefined): void {
  if (isLocale(locale) && locale !== getLocale()) {
    setModuleLocale(locale);
  }
}
